// src/app/page/login-page/login-page.component.ts
import { Component, ChangeDetectionStrategy } from '@angular/core';
import { FormGroup, FormControl, Validators } from '@angular/forms';
import { Router } from "@angular/router";
import { catchError, switchMap, filter, take } from 'rxjs/operators';

import { AppConstants } from 'src/app/constants/app-constants';
import { FormControlDefinition } from 'src/app/model/FormControlDefinition';
import { UserService } from 'src/app/services/user/user.service';
import { AuthService } from 'src/app/services/auth/auth.service';
import { of } from 'rxjs';

@Component({
    selector: 'app-login',
    templateUrl: './login-page.component.html',
    styleUrls: ['./login-page.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class LoginPageComponent {
  public loginFormGroup = new FormGroup({
    email:    new FormControl('', [ Validators.required, Validators.email ]),
    password: new FormControl('', Validators.required)
  });

  public loginFormDefinition: FormControlDefinition[] = [
    {
      title: "Email",
      name: "email",
      type: "email",
      placeholder: "",
      control: this.loginFormGroup.controls["email"],
      editable: true
    },
    {
      title: "Password",
      name: "password",
      type: "password",
      placeholder: "",
      control: this.loginFormGroup.controls["password"],
      editable: true
    }
  ];

  public clickedLogin = false;
  public loading      = false;
  public loginError   = '';

  constructor(
    private _userService: UserService,
    private _authService: AuthService,
    private _router: Router,
  ) {}

  /**
   * On component initialization check query params for user status messages
   */
  ngOnInit(): void {
    this._router.routerState.root.queryParams.subscribe(params => {
      if (params['status'] === 'deactivated') {
        this.loginError = 'Your account has been deactivated. Please contact support.';
      } else if (params['status'] === 'terminated') {
        this.loginError = 'Your account has been terminated. Please contact support.';
      }
    });
  }

  /**
   * Handle user login 
   */
  public login(): void {
    if (this.clickedLogin) return;
    this.clickedLogin = true;
    this.loading      = true;
    this.loginError   = '';

    // Hit authentication endpoint first, then wait for user object
    this._authService.authenticate(this.loginFormGroup.value.email!, this.loginFormGroup.value.password!).pipe(
      // If authentication succeeds, wait for the updated user object
      switchMap(() => 
        this._userService.user$.pipe(
          filter(user => user !== undefined), // Wait for a defined user
          take(1) // Take only the first emission to avoid multiple subscriptions
        )
      ),
      catchError(err => {
        console.error('[LOGIN]: authenticate error', err);
        
        if (err.status === 401 || err.status === 500) { // treat 500 from the auth endpoint as "wrong credentials"
          this.loginError = 'Wrong username/password';
        } else if (err.error.message.includes("deactivated") ?? false) { // If the user is deactivated, show a specific message
          this.loginError = 'Your account has been deactivated. Please contact support.';
        } else if (err.error && err.error.status === "failed" && err.error.message) { // If a nice error message is provided, use it
          this.loginError = err.error.message;
        } else { // Otherwise default to a generic error message
          this.loginError = 'Login failed. Please try again.';
        }

        // Reset loading state variables
        this.loading      = false;
        this.clickedLogin = false;

        return of(null); // Return null to indicate failure
      }),
    ).subscribe(user => {
      // Reset loading state variables
      this.loading      = false;
      this.clickedLogin = false;
    });
  }

  public resetPassword(): void {
    this._router.navigate([ AppConstants.MENU_FORGOT_PASSWORD_REQUEST ]);
  }
}
